// Pieces the three decision cards share: the status line, the blocked reason,
// the unknown-outcome block and the notice after a conflict. Every line comes
// from a typed field; none is chosen by reading text.
import type { AppApprovalsCopy } from '@/i18n/ns/app-approvals';
import { formatCopy } from '@/i18n/ns';
import { Callout } from '@/components/ui/content';
import { OriginTag, type StatusTone } from '@/components/ui/kit';
import type { DecisionBlock, DecisionOrigin, DecisionState } from './app-decision-types';
import type { DecisionNotice } from './use-app-decision';

export type DecisionView = DecisionState | 'unknown';

/** What the card shows: an outcome the server could not confirm outranks any state. */
export function decisionView(
  record: { state: DecisionState } & DecisionBlock,
  lost: boolean,
): DecisionView {
  return lost || record.outcome === 'unknown' ? 'unknown' : record.state;
}

/**
 * Whether the card may offer a decision. A record blocked on anything but the
 * approval itself (budget, provider, no trusted viewer) is not decidable here,
 * whatever else the record claims; neither is one whose outcome is not pending.
 */
export function isDecidable(
  record: { state: DecisionState; canDecide: boolean } & DecisionBlock,
  lost: boolean,
) {
  return (
    !lost &&
    record.state === 'pending' &&
    record.canDecide &&
    record.outcome === 'pending' &&
    (record.blockedReason === null || record.blockedReason === 'approval')
  );
}

export function statusLabel(copy: AppApprovalsCopy, view: DecisionView): string {
  switch (view) {
    case 'pending':
      return copy.needsYou;
    case 'approved':
    case 'running':
    case 'applied':
      return copy.approved;
    case 'rejected':
      return copy.rejected;
    case 'expired':
      return copy.expired;
    case 'stale':
      return copy.stale;
    case 'unknown':
      return copy.unknownTitle;
  }
}

export function statusTone(view: DecisionView): StatusTone {
  switch (view) {
    case 'approved':
    case 'running':
    case 'applied':
      return 'success';
    case 'pending':
    case 'stale':
    case 'unknown':
      return 'warning';
    default:
      return 'neutral';
  }
}

export function BlockedLine({ copy, block }: { copy: AppApprovalsCopy; block: DecisionBlock }) {
  if (block.outcome === 'unknown' || !block.blockedReason) return null;
  const line =
    block.blockedReason === 'approval'
      ? copy.blockedApproval
      : block.blockedReason === 'budget'
        ? copy.blockedBudget
        : block.blockedReason === 'viewer_context_required'
          ? copy.blockedViewer
          : copy.blockedProvider;
  return (
    <div data-blocked={block.blockedReason}>
      <Callout tone="warning">{line}</Callout>
    </div>
  );
}

/** The change may or may not have happened. Only a read-only status check is offered. */
export function UnknownBlock({
  copy,
  busy,
  still = null,
  onCheck,
}: {
  copy: AppApprovalsCopy;
  busy: boolean;
  /** What the last status check said when it settled nothing. */
  still?: 'pending' | 'unknown' | null;
  onCheck: () => void;
}) {
  return (
    <Callout
      tone="warning"
      title={copy.unknownTitle}
      actions={
        <button type="button" className="ws-text-button" disabled={busy} onClick={onCheck}>
          {copy.checkStatus}
        </button>
      }
    >
      {copy.unknownBody}
      {still && <p>{still === 'pending' ? copy.statusStillPending : copy.statusStillUnknown}</p>}
    </Callout>
  );
}

export function NoticeLine({ copy, notice }: { copy: AppApprovalsCopy; notice: DecisionNotice }) {
  if (!notice) return null;
  const line =
    notice === 'changed'
      ? copy.changedUnderYou
      : notice === 'expired'
        ? copy.expired
        : notice === 'unavailable'
          ? copy.cannotDecideNow
          : copy.notAllowed;
  return (
    <div data-notice={notice}>
      <Callout
        tone={
          notice === 'changed' || notice === 'unavailable'
            ? 'warning'
            : notice === 'expired'
              ? 'info'
              : 'danger'
        }
      >
        {line}
      </Callout>
    </div>
  );
}

/**
 * Where the request came from (the app, an Agent, or an outside client) as an
 * origin tag, and "for <person>" when a person triggered it. Both come from
 * typed fields; nothing is shown when the server states neither.
 */
export function OriginLine({
  copy,
  origin,
  forName,
}: {
  copy: AppApprovalsCopy;
  origin: DecisionOrigin | null;
  forName: string | null;
}) {
  if (!origin && !forName) return null;
  const text = !origin
    ? null
    : origin.kind === 'app'
      ? (origin.label ?? copy.originApp)
      : origin.kind === 'agent'
        ? (origin.label ?? copy.originAgent)
        : formatCopy(copy.originClient, { client: origin.label ?? '' });
  return (
    <p className="ws-app-card-origin">
      {text && (
        <span data-origin={origin?.kind}>
          <OriginTag label={text} />
        </span>
      )}
      {forName && <span>{formatCopy(copy.originFor, { name: forName })}</span>}
    </p>
  );
}
